'use client'

import { useEffect, useMemo, useState } from 'react'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import clsx from 'clsx'

import { normalizeFaIconValue, normalizeImage, normalizeSectionId } from '@/components/sections/section-utils'
import { Container } from '@/components/sections/container'

function normalizeSectionTabs(items) {
  return (Array.isArray(items) ? items : [])
    .map((item, index) => ({
      title: item?.title || item?.name || '',
      summary: item?.summary || item?.description || '',
      description: item?.description || item?.summary || '',
      image: normalizeImage(item?.image),
      icon: item?.icon || '',
    }))
    .filter((item) => {
      const hasTitle = item.title.trim()
      const hasBody = item.summary.trim() || item.description.trim()
      const hasImage = Boolean(item.image?.src)
      return hasTitle && (hasBody || hasImage)
    })
}

function normalizeIconClasses(iconValue, fallback) {
  const className = normalizeFaIconValue(iconValue, fallback)
  return `fa-solid ${className}`
}

function PrimarySection({ sectionId, heading, intro, tabs }) {
  const [tabOrientation, setTabOrientation] = useState('horizontal')

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)')

    function onMediaQueryChange({ matches }) {
      setTabOrientation(matches ? 'vertical' : 'horizontal')
    }

    onMediaQueryChange(mediaQuery)
    mediaQuery.addEventListener('change', onMediaQueryChange)

    return () => {
      mediaQuery.removeEventListener('change', onMediaQueryChange)
    }
  }, [])

  return (
    <section
      id={sectionId}
      aria-label="Features for running your books"
      className="relative overflow-hidden bg-primary pt-20 pb-28 sm:py-32"
    >
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-primary via-dark to-mid opacity-90" />
      <div
        className="pointer-events-none absolute -left-1/4 top-1/4 h-[28rem] w-[28rem] rounded-full bg-light/55 blur-3xl -z-10"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute right-[-14rem] top-[-6rem] h-[32rem] w-[32rem] rounded-full bg-mid/45 blur-3xl -z-10"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute right-[-4rem] bottom-[-8rem] h-[26rem] w-[26rem] rounded-full bg-dark/45 blur-3xl -z-10"
        aria-hidden="true"
      />
      <Container className="relative">
        {(heading || intro) ? (
          <div className="max-w-2xl md:mx-auto md:text-center xl:max-w-none">
            {heading ? (
              <h2 className="font-display text-3xl tracking-tight text-white sm:text-4xl md:text-5xl">
                {heading}
              </h2>
            ) : null}
            {intro ? <p className="mt-6 text-lg tracking-tight text-light">{intro}</p> : null}
          </div>
        ) : null}

        <TabGroup
          className="mt-16 grid grid-cols-1 items-center gap-y-2 pt-10 sm:gap-y-6 md:mt-20 lg:grid-cols-12 lg:pt-0"
          vertical={tabOrientation === 'vertical'}
        >
          {({ selectedIndex }) => (
            <>
              <div className="-mx-4 flex overflow-x-auto pb-4 sm:mx-0 sm:overflow-visible sm:pb-0 lg:col-span-5">
                <TabList className="relative z-10 flex gap-x-4 px-4 whitespace-nowrap sm:mx-auto sm:px-0 lg:mx-0 lg:block lg:gap-x-0 lg:gap-y-1 lg:whitespace-normal">
                  {tabs.map((feature, featureIndex) => (
                    <div
                      key={feature.title}
                      className={clsx(
                        'group relative rounded-full px-4 py-1 lg:rounded-l-xl lg:rounded-r-none lg:p-6',
                        selectedIndex === featureIndex
                          ? 'bg-white lg:bg-white/10 lg:ring-1 lg:ring-white/10 lg:ring-inset'
                          : 'hover:bg-white/10 lg:hover:bg-white/5',
                      )}
                    >
                      <h3>
                        <Tab
                          className={clsx(
                            'font-display text-lg data-selected:not-data-focus:outline-hidden',
                            selectedIndex === featureIndex
                              ? 'text-mid lg:text-white'
                              : 'text-light hover:text-white lg:text-white',
                          )}
                        >
                          <span className="absolute inset-0 rounded-full lg:rounded-l-xl lg:rounded-r-none" />
                          {feature.title}
                        </Tab>
                      </h3>
                      <p
                        className={clsx(
                          'mt-2 hidden text-sm lg:block',
                          selectedIndex === featureIndex
                            ? 'text-white'
                            : 'text-light group-hover:text-white',
                        )}
                      >
                        {feature.summary}
                      </p>
                    </div>
                  ))}
                </TabList>
              </div>
              <TabPanels className="lg:col-span-7">
                {tabs.map((feature) => (
                  <TabPanel key={feature.title} unmount={false}>
                    <div className="relative sm:px-6 lg:hidden">
                      <div className="absolute -inset-x-4 -top-26 -bottom-17 bg-white/10 ring-1 ring-white/10 ring-inset sm:inset-x-0 sm:rounded-t-xl" />
                      <p className="relative mx-auto max-w-2xl text-base text-white sm:text-center">
                        {feature.summary}
                      </p>
                    </div>
                      <div className="mt-10 w-180 overflow-hidden rounded-xl bg-light/20 shadow-xl shadow-dark/20 sm:w-auto lg:mt-0 lg:w-271.25">
                      {feature.image?.src ? (
                        <img
                          src={feature.image.src}
                          alt={feature.image.alt || feature.title}
                          className="w-full"
                          loading="lazy"
                        />
                      ) : null}
                    </div>
                  </TabPanel>
                ))}
              </TabPanels>
            </>
          )}
        </TabGroup>
      </Container>
    </section>
  )
}

function SecondaryFeature({ feature, isActive, className, ...props }) {
  const secondaryDescription = feature.description || feature.summary

  return (
    <div className={clsx(className, !isActive && 'opacity-75 hover:opacity-100')} {...props}>
      <div
        className={clsx(
          'grid h-9 w-9 place-items-center rounded-lg',
          isActive ? 'bg-primary' : 'bg-mid',
        )}
      >
        <i
          aria-hidden="true"
          className={clsx(
            normalizeIconClasses(feature.icon, 'circle-check'),
            'text-[16px] leading-none text-white',
          )}
        />
      </div>
      <h3
        className={clsx(
          'mt-6 text-xl font-medium',
          isActive ? 'text-primary' : 'text-mid',
        )}
      >
        {feature.name}
      </h3>
      {secondaryDescription ? <p className="mt-4 text-sm text-mid">{secondaryDescription}</p> : null}
    </div>
  )
}

function FeaturesMobile({ features }) {
  return (
    <div className="-mx-4 mt-20 flex flex-col gap-y-10 overflow-hidden px-4 sm:-mx-6 sm:px-6 lg:hidden">
      {features.map((feature) => (
        <div key={feature.title} className="mx-auto max-w-2xl">
          <SecondaryFeature feature={feature} isActive className="" />
          <div className="relative mt-10 pb-10">
            <div className="absolute -inset-x-4 top-8 bottom-0 bg-light/30 sm:-inset-x-6" />
            <div className="relative mx-auto w-211 overflow-hidden rounded-xl bg-white shadow-lg ring-1 shadow-dark/10 ring-dark/10">
              {feature.image?.src ? (
                <img
                  src={feature.image.src}
                  alt={feature.image.alt || feature.title}
                  className="w-full"
                />
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function FeaturesDesktop({ features }) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  return (
    <TabGroup
      selectedIndex={selectedIndex}
      onChange={setSelectedIndex}
      className="hidden lg:mt-20 lg:block"
    >
      <TabList className="grid grid-cols-3 gap-x-8">
        {features.map((feature, featureIndex) => (
          <SecondaryFeature
            key={feature.title}
            feature={{
              ...feature,
              name: (
                <Tab className="data-selected:not-data-focus:outline-hidden">
                  <span className="absolute inset-0" />
                  {feature.title}
                </Tab>
              ),
            }}
            isActive={featureIndex === selectedIndex}
            onMouseEnter={() => setSelectedIndex(featureIndex)}
            onFocus={() => setSelectedIndex(featureIndex)}
            className="relative"
          />
        ))}
      </TabList>
      <TabPanels className="relative mt-20 overflow-hidden rounded-4xl bg-light/30 px-14 py-16 xl:px-16">
        <div className="-mx-5 flex">
          {features.map((feature, featureIndex) => (
            <TabPanel
              static
              key={feature.title}
              className={clsx(
                'px-5 transition duration-500 ease-in-out data-selected:not-data-focus:outline-hidden',
                featureIndex !== selectedIndex && 'opacity-60',
              )}
              style={{ transform: `translateX(-${selectedIndex * 100}%)` }}
              aria-hidden={featureIndex !== selectedIndex}
            >
              <div className="w-211 overflow-hidden rounded-xl bg-white shadow-lg ring-1 shadow-dark/10 ring-dark/10">
                {feature.image?.src ? (
                  <img
                    src={feature.image.src}
                    alt={feature.image.alt || feature.title}
                    className="w-full"
                  />
                ) : null}
              </div>
            </TabPanel>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-0 rounded-4xl ring-1 ring-dark/10 ring-inset" />
      </TabPanels>
    </TabGroup>
  )
}

function SecondarySection({ sectionId, heading, intro, tabs }) {
  return (
    <section
      id={sectionId}
      aria-label="Features for simplifying everyday business tasks"
      className="pt-20 pb-14 sm:pt-32 sm:pb-20 lg:pb-32"
    >
      <Container>
        {(heading || intro) ? (
          <div className="mx-auto max-w-2xl md:text-center">
            {heading ? (
              <h2 className="font-display text-3xl tracking-tight text-dark sm:text-4xl">
                {heading}
              </h2>
            ) : null}
            {intro ? <p className="mt-4 text-lg tracking-tight text-primary">{intro}</p> : null}
          </div>
        ) : null}
        <FeaturesMobile features={tabs} />
        <FeaturesDesktop features={tabs} />
      </Container>
    </section>
  )
}

export function ImageTabsSection({ section, variant = 'primary' }) {
  const sectionId = normalizeSectionId(
    section,
    variant === 'secondary' ? 'secondary-features' : 'features',
  )
  const heading = section?.heading || ''
  const intro = section?.intro || ''
  const tabs = useMemo(() => normalizeSectionTabs(section?.items), [section?.items])

  if (!tabs.length) {
    return null
  }

  if (variant === 'secondary') {
    return <SecondarySection heading={heading} intro={intro} sectionId={sectionId} tabs={tabs} />
  }

  return <PrimarySection heading={heading} intro={intro} sectionId={sectionId} tabs={tabs} />
}
